<template>
  <div class="tender-information">
    <div
      v-if="$slots.title"
      class="mb-4">
      <slot name="title"/>
    </div>
    <div class="tender-information__content">
      <CollapseBlock
        v-if="isDeveloper"
        transparent
        :value.sync="show.devProps">
        <div>
          {{ fieldErrors }}
        </div>
      </CollapseBlock>
      <template v-if="tenderMutable">
        <CollapseBlock
          transparent
          :value.sync="show.tenderInformation">
          <div
            slot="title"
            class="tender-information__collapse-title mt-0">
            {{ $t('Tenders.TenderInformation') }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.tenderInformation ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <sFormItem
            v-if="availableOrganizations && availableOrganizations.length > 1"
            class="tender-information__content-item mb-3"
            :error="fieldErrors.organizationItId"
            :label="$t('Tenders.TenderOrganizer')"
            :show-message="false">
            <sSelect
              class="width-100"
              data-qa="tender-organizer"
              :disabled="loading || !tender || !!tender.tenderId"
              filterable
              :placeholder="$t('Tenders.TenderOrganizer')"
              :value="currentOrganization"
              :value-key="'organizationItId'"
              @update:value="changeCurrentOrganization">
              <sOption
                v-for="item in availableOrganizations"
                :key="`tender-organizer-${item.organizationItId}`"
                :data-qa="`tender-organizer-item-${item.organizationItId}`"
                :label="item.title"
                :value="item"/>
            </sSelect>
          </sFormItem>
          <sFormItem
            class="tender-information__content-item mb-3"
            :error="fieldErrors.tenderSubject"
            :label="$t('Planning.Name')"
            prop="tenderSubject">
            <sInput
              clearable
              data-qa="tenderSubject"
              :max-length="4000"
              max-rows="20"
              :placeholder="$t('Tenders.EnterTenderName')"
              rows="1"
              style="width: 400px;"
              type="textarea"
              :value.sync="tenderMutable.tenderSubject"/>
          </sFormItem>
          <div style="position: relative;">
            <sFormItem
              ref="formItemBiddingTypeId"
              class="tender-information__content-item"
              :error="fieldErrors.biddingTypeId"
              :label="$t('Tenders.TenderType')"
              prop="biddingType.biddingTypeId">
              <sSelect
                clearable
                data-qa="biddingType"
                :disabled="load.prepare || (biddingType && biddingType.isFrameAuction || !spr.biddingTypes)"
                filterable
                :placeholder="$t('Tenders.TenderType')"
                style="width: 400px;"
                :value="tenderMutable.biddingType"
                value-key="biddingTypeId"
                @update:value="changeBidType">
                <sOption
                  v-for="item in spr.biddingTypes"
                  :key="`bidding-types-${item.biddingTypeId}`"
                  :data-qa="`biddingType-item-${item.biddingTypeId}`"
                  :label="item.titleRu"
                  :value="item">
                  <div>
                    <div>{{ item.titleRu }}</div>
                    <template v-if="isNotProduction">
                      <sChip
                        v-if="item.paidCommercialBidding"
                        background="var(--alert-warning-background)"
                        color="var(--alert-text-desc)">
                        paidCommercialBidding
                      </sChip>
                    </template>
                  </div>
                </sOption>
              </sSelect>
            </sFormItem>
            <BiddingTypeInfo
              v-if="biddingType && biddingType.biddingTypeId"
              class="tender-information__content-info-block"
              :type="biddingType"/>
          </div>
          <sFormItem
            ref="formClassificationCode"
            class="tender-information__content-item"
            :error="fieldErrors.classificationCode"
            :label="$t('Tenders.Category')"
            prop="classificationCode"
            :show-message="false">
            <CategoryDropdownTree
              ref="categoryTree"
              class="v-input"
              data-qa="classification"
              :disabled="biddingType && biddingType.isFrameAuction"
              input-type="textarea"
              :label="$t('Tenders.Category')"
              :model.sync="tenderMutable.classificationCode"
              :searched="tenderMutable.tenderSubject"
              :used="spr.additionalCategories.map(el => el.classificationCode)"
              @clear="spr.additionalCategories=[]"
              @update:model="updateCategory"/>
            <sTooltip
              :border="false"
              :content="$t('Tenders.categoryTemplateHint')"
              :disabled="biddingTypeIsPublic"
              :enterable="false"
              type="white">
              <CategoryTemplate
                :curent-value="spr.classificationTemplate"
                :disabled="!biddingTypeIsPublic"
                :value.sync="classificationTemplateMutable"/>
            </sTooltip>
            <AddAdditionalCategories
              v-if="tenderMutable.classificationCode && biddingTypeIsPublic"
              :additional_categories.sync="spr.additionalCategories"
              :used="tenderMutable.classificationCode"/>
          </sFormItem>
        </CollapseBlock>

        <CollapseBlock
          transparent
          :value.sync="show.completionProposals">
          <div
            slot="title"
            class="tender-information__collapse-title">
            {{ biddingType.withoutPublication ? $t('Tenders.AdditionalTenderDescription') : $t('Tenders.CompletionProposals') }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.completionProposals ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <div>
            <TenderTerms
              v-if="!biddingType.withoutPublication"
              class="tender-information__content-item"
              :date-min="mixin_tenderDateMin.dateString"
              :datelabel="isAuction ? 'Tenders.AuctionClosing' : 'Tenders.EndDate'"
              :tender.sync="tenderMutable"/>

            <sFormItem
              class="tender-information__content-item"
              :error="fieldErrors.tenderDescription"
              :label="biddingType.withoutPublication ? '' : $t('Tenders.AdditionalTenderDescription')"
              prop="tenderDescription">
              <sInput
                clearable
                data-qa="description"
                :max-length="4000"
                max-rows="20"
                :placeholder="$t('Tenders.addedTenderDescription')"
                rows="5"
                type="textarea"
                :value.sync="tenderMutable.tenderDescription"/>
            </sFormItem>
          </div>
        </CollapseBlock>

        <CollapseBlock
          v-if="$can('use', 'availableAdditionalFields')"
          transparent
          :value.sync="show.additionalFields">
          <div
            slot="title"
            class="tender-information__collapse-title">
            {{ $t('AdditionalFields.AdditionalFields') }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.additionalFields ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <div class="text-left">
            <template v-if="additionalFieldsTenderExists">
              <AdditionalFieldInput
                v-for="item in additionalFieldsTenderExists"
                :key="item.id"
                :field="item"
                :validation-prop="`existing_fields.${item.id}`"
                @update:value="($event) => changeAdditionalField(item, $event)"/>
            </template>
            <template v-if="additionalFieldsTenderNew">
              <AdditionalFieldInput
                v-for="item in additionalFieldsTenderNew"
                :key="item.id"
                :field="item"
                :validation-prop="`additional_fields.${item.id}`"
                @update:value="($event) => changeAdditionalField(item, $event, true)"/>
            </template>
            <AdditionalFields
              ref="additionalFields"
              :additional_fields.sync="additionalFieldsTender"
              binding-level="TENDER"
              :entity="tender"/>
          </div>
        </CollapseBlock>
        <CollapseBlock
          transparent
          :value.sync="show.contactPerson">
          <div
            slot="title"
            class="tender-information__collapse-title">
            {{ $t('Tenders.ContactPerson').charAt(0).toUpperCase() + $t('Tenders.ContactPerson').slice(1) }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.contactPerson ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <TenderContactPerson
            class="tender-information__content-item"
            :value.sync="tenderMutable"
            @clearValidate="$emit('clearValidate')"/>
        </CollapseBlock>
        <CollapseBlock
          v-if="(biddingType && biddingType.canChangeCurrencyOwner)
            || !tender.currencyOwner
            || !tender.currencyOwner && $can('include', 'vat')
            || (tender.biddingType && tender.biddingType.allowRaising)
            || (biddingType && biddingType.allowedCurrenciesSelectionByOrganizer)"
          transparent
          :value.sync="show.budget">
          <div
            slot="title"
            class="tender-information__collapse-title">
            {{ $t('Tenders.Budget') }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.budget ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <div>
            <sFormItem
              v-if="biddingType && (biddingType.canChangeCurrencyOwner || biddingType.allowedCurrenciesSelectionByOrganizer)"
              :error="fieldErrors.currencyOwner"
              :label="$t('Tenders.WhoSpecifiesCurrency')"
              prop="currencyOwner">
              <sRadioGroup
                v-model="tenderMutable.currencyOwner"
                class="mt-2"
                data-qa="currencyOwner"
                row>
                <sRadio
                  data-qa="currencyOwner-organizer"
                  :label="0">
                  {{ $t('Tenders.Organizer') }}
                </sRadio>
                <sRadio
                  data-qa="currencyOwner-member"
                  :label="1">
                  {{ $t('Tenders.Member') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <sFormItem
              v-if="!tender.currencyOwner"
              :error="fieldErrors.tenderCurrency"
              prop="tenderCurrency">
              <sTooltip
                :border="false"
                :content="$t('Tenders.TenderHasBidsCurrencyDisabled')"
                :disabled="!tender.hasBids"
                type="dark">
                <span class="h5-headline mb-5">{{ $t('Tenders.ChooseCurrency') }}</span>
                <label
                  class="sFormItem__label pb-1 ml-3"
                  for="tender-currency">
                  <span class="h8-text black--text">{{ $t('Tenders.Currency') }}</span>
                </label>
                <sSelect
                  data-qa="tender-currency"
                  :disabled="tender.hasBids"
                  filterable
                  :loading="load.currencies"
                  :placeholder="$t('Tenders.Currency')"
                  :popper-append-to-body="false"
                  popper-class="select-tender-currency"
                  style="width: 400px;"
                  :value.sync="tenderMutable.tenderCurrency">
                  <sOption
                    v-for="item in spr.currencies"
                    :key="`tender-currency-${item.designation}`"
                    :data-qa="`tender-currency-item-${item.designation}`"
                    :label="item.title"
                    :value="item.designation"/>
                </sSelect>
              </sTooltip>
            </sFormItem>
            <sFormItem
              v-else-if="biddingType && tender.biddingType && tender.biddingType.allowedCurrenciesSelectionByOrganizer"
              :error="fieldErrors.allowedCurrencies"
              prop="allowedCurrencies">
              <sSelect
                data-qa="allowed-currencies"
                :disabled="tender.hasBids || tender.currencies?.length === 0"
                filterable
                :loading="load.currencies"
                multiple
                :placeholder="$t('Tenders.Currency')"
                :popper-append-to-body="false"
                popper-class="select-tender-currency"
                style="width: 400px;"
                :value.sync="tenderMutable.allowedCurrencies">
                <sOption
                  v-for="item in spr.currencies"
                  :key="`tender-currency-${item.designation}`"
                  :data-qa="`tender-currency-item-${item.designation}`"
                  :label="item.title"
                  :value="item.designation"/>
              </sSelect>
            </sFormItem>

            <sFormItem
              v-if="!tender.currencyOwner && $can('include', 'vat')"
              :error="fieldErrors.vat"
              prop="vat">
              <div
                slot="label"
                class="h5-headline">
                {{ $t('Tenders.HowFormFinalBudget') }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.vat"
                class="mt-2"
                data-qa="tender-vat"
                row>
                <sRadio
                  data-qa="tender-vat-with"
                  :label="true">
                  {{ $t('Tenders.WithVAT') }}
                </sRadio>
                <sRadio
                  data-qa="tender-vat-without"
                  :label="false">
                  {{ $t('Tenders.WithoutVAT') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <sFormItem
              v-if="tender.biddingType && tender.biddingType.allowRaising"
              :error="fieldErrors.allowRaising"
              prop="allowRaising">
              <div
                slot="label"
                class="h5-headline">
                {{ biddingType.isSale ? 'Дозволити зниження ціни' : 'Дозволити підвищення ціни' }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.allowRaising"
                class="mt-2"
                data-qa="tender-allowRaising"
                row>
                <sRadio
                  data-qa="tender-allowRaising-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-allowRaising-no"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
          </div>
        </CollapseBlock>
        <CollapseBlock
          v-if="!biddingType.isFrameFirstStage || useProlongationRule"
          transparent
          :value.sync="show.deliveryTime">
          <div
            slot="title"
            class="tender-information__collapse-title">
            {{ $t('Tenders.DeliveryTimeTitle') }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.deliveryTimeTitle ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <div>
            <DeliveryPeriod
              v-if="!biddingType.isFrameFirstStage"
              class="tender-information__content-item"
              :tender.sync="tenderMutable"/>
            <ProlongRules
              v-if="useProlongationRule"
              class="tender-information__content-item"
              :rules="spr.prolongationRules"
              :tender.sync="tenderMutable">
              <template
                slot="title">
                {{ $t('Tenders.AutoContinuationStatus',
                      {
                        status: isAuction ? $t('Tenders.OfAuction') : $t('Tenders.OfAcceptanceOfOffers')
                      })
                }}
              </template>
              <template
                slot="description">
                {{ $t('Tenders.AutomaticallyContinueToReceiveOffers',
                      {status: isAuction ? $t('Tenders.OfAuction') : $t('Tenders.OfAcceptanceOfOffers') })
                }}
              </template>
            </ProlongRules>
          </div>
        </CollapseBlock>
        <CollapseBlock
          v-if="!biddingType.withoutPublication"
          transparent
          :value.sync="show.additionally">
          <div
            slot="title"
            class="tender-information__collapse-title">
            {{ $t('Tenders.Additionally') }}
            <sIcon
              class="ml-2 my-auto"
              :rotate="show.additionally ? '-180' : '0'"
              size="14"
              type="arrow"/>
          </div>
          <div>
            <sFormItem
              v-if="!isGroupLotsEnabled"
              class="tender-information__content-item"
              :error="fieldErrors.bidOnAllLots"
              prop="bidOnAllLots">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.SubmitBidsForAllLots') }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.bidOnAllLots"
                class="mt-2"
                data-qa="tender-bidOnAllLots"
                row>
                <sRadio
                  data-qa="tender-bidOnAllLots-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-bidOnAllLots-no"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <sFormItem
              v-else
              class="tender-information__content-item"
              :error="fieldErrors.bidOnAllLotInGroup"
              prop="bidOnAllLotInGroup">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.SubmitBidsForAllLotInGroup') }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.bidOnAllLotInGroup"
                class="mt-2"
                data-qa="tender-bidOnAllLotInGroup"
                row>
                <sRadio
                  data-qa="tender-bidOnAllLotInGroup-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-bidOnAllLotInGroup-no"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <sFormItem
              class="tender-information__content-item"
              :error="fieldErrors.mandatoryFullBid"
              prop="mandatoryFullBid">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.mandatoryFullBid') }}?
              </div>
              <sRadioGroup
                v-model="tenderMutable.mandatoryFullBid"
                class="mt-2"
                data-qa="tender-mandatoryFullBid"
                row>
                <sRadio
                  data-qa="tender-mandatoryFullBid-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-mandatoryFullBid-NO"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>


            <sFormItem
              v-if="$can('set', 'hasUrgentPurchaseFlag')"
              class="tender-information__content-item"
              :error="fieldErrors.isUrgent"
              prop="isUrgent">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.UrgentPurchase') }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.isUrgent"
                class="mt-2"
                data-qa="tender-urgent-purchase"
                row>
                <sRadio
                  data-qa="tender-urgent-purchase-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-urgent-purchase-NO"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <sFormItem
              class="tender-information__content-item"
              :error="fieldErrors.requiedAttachments"
              prop="requiedAttachments">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.FileAttachmentWhenSubmitting') }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.requiedAttachments"
                class="mt-2"
                data-qa="tender-requiedAttachments"
                row>
                <sRadio
                  data-qa="tender-requiedAttachments-necessarily"
                  :label="true">
                  {{ $t('Tenders.Necessarily') }}
                </sRadio>
                <sRadio
                  data-qa="tender-requiedAttachments-optional"
                  :label="false">
                  {{ $t('Tenders.Optional') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <div
              v-if="$can('hide', 'BestPriceBeforeBidding') && biddingType && !biddingType.isRating && !biddingType.isAnalysisMarket"
              class="flex flex-between mb-6"
              style="position: relative;">
              <sFormItem
                class="mb-0"
                :error="fieldErrors.hideBestPrice"
                prop="hideBestPrice"
                style="max-width: calc(100% - 315px);">
                <div
                  slot="label"
                  class="h5-headline black--text">
                  {{ $t('Tenders.HideBestPriceInTenderBeforeTheFirstBid') }}
                </div>
                <sRadioGroup
                  v-model="tenderMutable.hideBestPrice"
                  class="mt-2"
                  data-qa="tender-hideBestPrice"
                  row>
                  <sRadio
                    data-qa="tender-hideBestPrice-yes"
                    :label="true">
                    {{ $t('Tenders.Yes') }}
                  </sRadio>
                  <sRadio
                    data-qa="tender-hideBestPrice-no"
                    :label="false">
                    {{ $t('Tenders.No') }}
                  </sRadio>
                </sRadioGroup>
              </sFormItem>
              <sAlert
                class="tender-information__content-info-block">
                {{ $t('Tenders.HidingTheBestPrice') }}
                <template slot="desc">
                  {{ $t('Tenders.HidingTheBestPriceText') }}
                </template>
              </sAlert>
            </div>
            <sFormItem
              v-if="biddingType.isSale && !$can('set', 'AllowIndividualsParticipation', 'NONE')"
              class="tender-information__content-item"
              :error="fieldErrors.areIndividualsAllowed"
              prop="areIndividualsAllowed">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.AllowIndividualsParticipation') }}
              </div>
              <sRadioGroup
                v-model="tenderMutable.areIndividualsAllowed"
                class="mt-2"
                data-qa="tender-areIndividualsAllowed"
                :disabled="$can('set', 'AllowIndividualsParticipation', 'ALWAYS')"
                row>
                <sRadio
                  data-qa="tender-areIndividualsAllowed-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-areIndividualsAllowed-no"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
            <sFormItem
              v-if="!$can('set', 'IsAllowParticipantCancelBid', 'ALWAYS_ON')"
              class="tender-information__content-item"
              :error="fieldErrors.allowParticipantBidCancellation"
              prop="allowParticipantBidCancellation">
              <div
                slot="label"
                class="h5-headline black--text">
                {{ $t('Tenders.AnnulateBidParticipant') }}?
              </div>
              <sRadioGroup
                v-model="tenderMutable.allowParticipantBidCancellation"
                class="mt-2"
                data-qa="tender-allowParticipantBidCancellation"
                :disabled="$can('set', 'IsAllowParticipantCancelBid', 'ALWAYS_OFF')"
                row>
                <sRadio
                  data-qa="tender-allowParticipantBidCancellation-yes"
                  :label="true">
                  {{ $t('Tenders.Yes') }}
                </sRadio>
                <sRadio
                  data-qa="tender-allowParticipantBidCancellation-no"
                  :label="false">
                  {{ $t('Tenders.No') }}
                </sRadio>
              </sRadioGroup>
            </sFormItem>
          </div>
        </CollapseBlock>
      </template>
    </div>
  </div>
</template>
<script>
import BiddingTypeInfo from 'components/tenders/BiddingTypeInfo.vue'
import CategoryTemplate from 'components/tenders/CategoryTemplate.vue'
import ProlongRules from 'components/tenderCreation/block/ProlongRules.vue'
import DeliveryPeriod from 'components/tenderCreation/block/DeliveryPeriod.vue'
import AdditionalFields from 'components/additional fields/AdditionalFields.vue'
import CategoryDropdownTree from 'components/common/dialogs/CategoryDropDownTree.vue'
import AdditionalFieldInput from 'components/additional fields/AdditionalFieldInput.vue'
import TenderContactPerson from 'components/tenderCreation/block/TenderContactPerson.vue'
import AddAdditionalCategories from 'components/tenders/dialogs/AddAdditionalCategories.vue'
import TenderTerms from 'components/tenderCreation/block/TenderTerms.vue'
import CollapseBlock from 'components/common/CollapseBlock.vue'

import { userMixin, storageMixin } from 'mixins'
import editTenderMixin from 'mixins/editTenderMixin'

import SmarttenderApi from '@/api/smarttenderApi'
const api = new SmarttenderApi()

import { mapGetters } from 'vuex'

export default {
  name: 'TenderInformation',
  mixins: [ userMixin, storageMixin, editTenderMixin ],
  data() {
    return {
      youcontrolEmails: [],
      show: {
        devProps: true,
        tenderInformation: true,
        completionProposals: true,
        additionalFields: false,
        contactPerson: true,
        budget: false,
        deliveryTime: false,
        additionally: false,
      },
      fieldOnBlock: [ // соответсвие свойств формы и блоков где они размещены
        {
          block: 'tenderInformation',
          fields: ['tenderSubject', 'biddingType.biddingTypeId', 'classificationCode', 'tenderDescription']
        },
        {
          block: 'completionProposals',
          fields: ['tenderPeriodTo', 'tenderDescription', 'scheduledStart', 'previousMailing']
        },
        {
          block: 'additionalFields',
          fields: ['existing_fields', 'additional_fields']
        },
        {
          block: 'contactPerson',
          fields: ['contactPersonId', 'additionalContactPersons']
        },
        {
          block: 'budget',
          fields: ['currencyOwner', 'tenderCurrency', 'vat', 'allowRaising', 'allowedCurrencies']
        },
        {
          block: 'deliveryTime',
          fields: ['deliveryPeriodFrom']
        },
        {
          block: 'additionally',
          fields: ['bidOnAllLotInGroup', 'bidOnAllLots', 'isUrgent', 'requiedAttachments', 'hideBestPrice', 'areIndividualsAllowed', 'allowParticipantBidCancellation']
        },
      ],
      spr: {
        biddingTypes: [],
        currencies: [],
        additionalCategories: [],
        oldAdditionalCategories: [],
        classificationTemplate: {
          mainCategory: {},
          additionalCategories: {}
        },
      },

      load: {
        biddingTypes: false, // по идее, нигде не используется
        prepare: true,
        currencies: true // загрузка валюты
      }
    }
  },
  methods: {
    beforeSendTender() {
      const result = this.$_.cloneDeep(this.tender)

      // для доп. контактных особоб созданых вручную нужно передавать ключ 0
      if (result && result.additionalContactPersons?.length && !result.additionalContactPersons[0].contactId) {
        result.additionalContactPersons[0].contactId = result.additionalContactPersons[0].contactId ?? 0
      }

      this.$emit('update:tender', result)
    },
    /**
     * Вызывается снаружи при валидации, что б развернуть свернутые блоки
     */
    checkAndShowCollapse(errors) {
      let needValidate = false
      errors.forEach(error => {
        const foundBlock = this.fieldOnBlock.find(block =>
          block.fields.some(field => error.field.includes(field))
        )

        if (foundBlock && !this.show[foundBlock.block]) {
          this.show[foundBlock.block] = true
          needValidate = true
        }
      })
      if (needValidate) {
        this.$emit('startValidate')
      }
    },
    cancelTenderChange() {
      this.spr.additionalCategories = this.$_.cloneDeep(this.spr.oldAdditionalCategories)
    },
    customCheckChange() {
      return !this.$_.isEqual(this.spr.additionalCategories, this.spr.oldAdditionalCategories)
    },
    changeAdditionalField(item, value, isNew = false) {
      if (!item) {
        return
      }
      const propName = isNew ? 'additionalFieldsTenderNew' : 'additionalFieldsTenderExists'

      const oldField = this[propName].find(i => i.id === item.id)

      if (!oldField) {
        this.$notice.info({
          title: this.$t('Common.ErrorTitle'),
          message: 'Не знайдене доп. поле. Повідомте розробникам',
          duration: 10
        })
        return
      }

      if (oldField.value === value) {
        return
      }

      const updatedFields = [...this[propName]]
      const fieldToUpdate = updatedFields.find(i => i.id === item.id)
      if (fieldToUpdate) {
        fieldToUpdate.value = value
      }
      if (JSON.stringify(this[propName]) === (updatedFields)) {
        return
      }

      if (isNew) {
        this.additionalFieldsTenderNew = updatedFields
      } else {
        this.additionalFieldsTenderExists = updatedFields
      }
    },
    async afterSendTender(tender) {
      //TODO: по идее, нужно проверить что доп. поля изменились и только тогда отправлять запрос
      await this.mixin_bindAdditionalFields(tender)

      if (this.customCheckChange() && this.biddingTypeIsPublic) {
        await api.addAdditionalCategoriesToTender(this.classificationTemplateMutable.additionalCategories, tender.tenderId )
      }
    },
    updateCategory(val) {
      this.mixin_sendChangeFormItem('formClassificationCode', val)
      this.getFieldsByBindConditions()
    },
    async changeBidType(ev) {
      ev = ev || {}
      if (ev.isSale !== this.tender.biddingType.isSale) {
        this.tenderMutable.areIndividualsAllowed = null
      }
      if (this.tender.biddingTypeId !== ev.biddingTypeId) {
        const oldBiddingType = {...this.tenderMutable.biddingType}
        this.tenderMutable.biddingTypeId = ev.biddingTypeId
        this.tenderMutable.biddingType = ev
        this.mixin_sendChangeFormItem('formItemBiddingTypeId', ev)
        this.tenderMutable = await this.mixin_checkTenderBySettings(this.tenderMutable, oldBiddingType)
        if (ev.biddingTypeId) {
          this.getFieldsByBindConditions()
        }
      }
    },
    async getFieldsByBindConditions() {
      await this.mixin_getFieldsByBindConditions({
        classificationCode: this.tenderMutable?.classificationCode,
        biddingTypeId: this.tenderMutable?.biddingType?.biddingTypeId,
      })
    },
    createClassificationTemplate() {
      let mainCategory = {}
      let additionalCategories = []
      if (this.$refs.categoryTree?.category) {
        mainCategory = {
          code: this.$refs.categoryTree.category.classificationCode,
          title: this.$refs.categoryTree.category.title
        }
      }
      if (this.spr.additionalCategories) {
        additionalCategories = [...this.spr.additionalCategories].map(i => ({
          code: i.classificationCode,
          title: i.title
        }))
      }
      this.spr.classificationTemplate = {
        mainCategory,
        additionalCategories
      }
    },
    /**
     * проверяем наличие сохраненных контактов из YC.Market
     */
    checkYouControl() {
      const compains = JSON.parse(this.$_getPropFromLocalStorage('youcontrolTender', '[]', true) || '[]')
      if (!compains?.length) {
        return
      }


      if (this.$route.query?.step === '2' && !this.tender.tenderId) {
        this.$set(this, 'youcontrolEmails', compains.map(i => i.email))
      } else {
        const that = this
        let content = '<ul class="ml-5">'
        compains.forEach((compain) => {
          content += `<li>${compain.email}</li>`
        })
        content += '</ul>'
        this.$Modal.confirm({
          title: 'YC.Market',
          content:
            `${compains.length === 1 ? 'Був знайдений контакт отриманий' : 'Були знайдені контакти отримані'} від YC.Market</br>
            <b>Запросити ${compains.length === 1 ? 'його' : 'їх'} до тендеру?</b></br>${content}`,
          closable: true,
          okText: this.$t('Tenders.Yes'),
          cancelText: this.$t('Tenders.No'),
          dangerouslyUseHTMLString: true,
          onOk: () => {
            that.$set(that, 'youcontrolEmails', compains.map(i => i.email))
          },
          onCancel: () => {
            that.$_savePropToLocalStorage('youcontrolTender', null, true)
          },
        })
      }
    },
    async changeCurrentOrganization(value) {
      await this.changeOrganisation(value, this.currentOrganization)

      this.load.biddingTypes = true
      await this.$store.dispatch('setBiddingTypes')
        .then(res => this.checkBidTypes(res))
        .finally(() => this.load.biddingTypes = false)

      this.tenderMutable = await this.mixin_checkTenderBySettings(this.tenderMutable)

      if (this.currentOrganization.organizationItId !== value.organizationItId) {
        await this.changeCurrentOrganization(value)
      }
    },
    checkBidTypes(biddingTypes) {
      if (!this.biddingType?.isFrameAuction) {
        biddingTypes =  biddingTypes.filter(el => !el.isFrameAuction )
      }
      this.spr.biddingTypes = biddingTypes
    },
  },
  computed: {
    ...mapGetters({
      availableOrganizations: 'getAvailableOrganizations',
      currentOrganization: 'getCurrentOrganization',
      fieldErrors: 'editTenderStore/fieldErrors',
    }),
    useProlongationRule() {
      return this.biddingType?.prolongationRuleAvailable && (!this.biddingType?.isMultistage || this.isAuction)
    },
    tenderMutable: {
      get() {
        return this.tender
      },
      set(newValue) {
        this.$emit('update:tender', newValue)
      }
    },
    classificationTemplateMutable: {
      get() {
        let mainCategory = {}
        let additionalCategories = []
        if (this.tender.classificationCode) {
          mainCategory = this.tender.classificationCode
        }
        if (this.spr.additionalCategories?.length) {
          additionalCategories = [...this.spr.additionalCategories].map(i => (i.classificationCode))
        }
        return {
          mainCategory: mainCategory,
          additionalCategories: additionalCategories
        }
      },
      set(newValue) {
        this.tenderMutable.classificationCode = newValue.mainCategory.code
        if (this.biddingTypeIsPublic) {
          this.spr.additionalCategories = newValue.additionalCategories.map(i => ({
            classificationCode: i.code,
            title: i.title,
            id: i.code
          }))
        }
        this.updateCategory(newValue.mainCategory.code)
      }
    },
    biddingType() {
      return this.tender.biddingType || {}
    },
    biddingTypeIsPublic() {
      return this.biddingType?.isPublic
    },
  },
  components: {
    TenderTerms,
    ProlongRules,
    CollapseBlock,
    DeliveryPeriod,
    BiddingTypeInfo,
    AdditionalFields,
    CategoryTemplate,
    TenderContactPerson,
    CategoryDropdownTree,
    AdditionalFieldInput,
    AddAdditionalCategories
  },
  watch: {
    'tender.biddingType.prolongationRuleAvailable': {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.mixin_getProlongRules()
        } else {
          this.tenderMutable.prolongRuleCode = null
        }
      }
    },
    // TODO: решили отключить сохранение
    show: {
      deep: true,
      handler(newValue) {
        if (this.isDeveloper) {
          this.$_savePropToLocalStorage('newFlou_step1_collapse', JSON.stringify(newValue), true)
        }
      }
    },
    classificationTemplateMutable: {
      immediate: true,
      deep: true,
      handler() {
        this.createClassificationTemplate()
      }
    }
  },
  async created() {
    this.$store.dispatch('editTenderStore/clearAdditionalFieldsConditions')
    this.$root.$on('cancelTenderChange', this.cancelTenderChange)
    if (this.isDeveloper) {
      this.show = JSON.parse(this.$_getPropFromLocalStorage('newFlou_step1_collapse', null, true)) || this.show
    }

    let tenderId = this.tender?.tenderId ?? (this.$route.params?.id || this.$route.query?.id)
    this.load.prepare = true
    this.load.currencies = true
    await this.$store.dispatch('setBiddingTypes')
      .then(res => this.checkBidTypes(res))
      .finally(() => this.load.prepare = false)
    await this.$store.dispatch('setCurrencies')
      .then(res => {
        if (!res.some(item => item.designation === this.tenderMutable.tenderCurrency)) {
          this.tenderMutable.tenderCurrency = 'UAH'
        }
        this.spr.currencies = res
      })
      .finally(() => this.load.currencies = false)

    if (tenderId && this.biddingTypeIsPublic) {
      this.$store.dispatch('getTenderItem', {id: tenderId, condition: true })
        .then(res => {
          this.spr.additionalCategories = res?.additionalCategories?.map(el => (
            {
              ...el,
              id: el.code,
              classificationCode: el.code,
              title: el.code + ' ' + el.title
            }))
          this.spr.oldAdditionalCategories = this.$_.cloneDeep(this.spr.additionalCategories)
          this.changeBidType(this.tender.biddingType ?? res.biddingType)
        })
    } else {
      this.checkYouControl()
    }
    this.createClassificationTemplate()
    this.tenderMutable = await this.mixin_checkTenderBySettings(this.tenderMutable)

    if (this.$can('use', 'availableAdditionalFields')) {
      await this.$store.dispatch('setAdditionalFieldsTypes')
      if (tenderId) {
        await this.getFieldsByBindConditions()
        await this.$store.dispatch('editTenderStore/getAdditionalFieldsByEntity', {tenderId})
        this.additionalFieldsLots = this.getter_additionalFieldsEntityLots
        await this.mixin_setAdditionalFieldsToLots(this.tenderMutable?.lots || [])
      }
      this.$root.$emit('createRuleAdditionalFields', {value: this.additionalFieldsTenderExists, exist: true, tender: true})
      this.$root.$emit('createRuleAdditionalFields', {value: this.additionalFieldsTenderNew, exist: false, tender: true})
    }
  },
  beforeDestroy() {
    this.$root.$off('cancelTenderChange', this.cancelTenderChange)
  },
  props: {
    tender: {
      type: Object
    },
    loading: {
      type: Boolean
    },
    rules: {
      type: Object
    }
  },
}
</script>
<style scoped lang="scss">
.testtt {
  opacity: 0.8;
}
.tender-information {
  max-width: 805px;
  margin: 0 auto;
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 100%;
  &__collapse-title {
    text-align: left;
    padding: 24px 0;
    font-size: 17px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0.17px;
    display: grid;
    grid-template-columns: auto 20px;
    gap: 5px;
  }
  &__content {
    background-color: var(--v-main__light_green-base);
    border-radius: 8px;
    padding: 0 30px 30px;
    overflow: auto;
    &-item {
      width: 400px;
      text-align: left;
      &-title {
        font-weight: 500;
        font-size: 17px;
        line-height: 22px;
        letter-spacing: 0.17px;
        padding: 24px 24px 24px 0;
        margin-top: 24px;
      }
    }
  }
  &__content-info-block {
    width: 315px;
    position: absolute;
    right: 0;
    top: 0;
  }
}
</style>
